import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { EmployeeLayout } from '../../components/EmployeeLayout';
import { employeeAPI, documentAPI } from '../../lib/api';
import { FileText, ExternalLink, CheckCircle, Circle } from 'lucide-react';
import { toast } from 'sonner';

function timeAgo(isoStr) {
  if (!isoStr) return '';
  const diff = Date.now() - new Date(isoStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(isoStr).toLocaleDateString();
}

export default function EmployeeDocuments() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  // Inline confirmation state per-document: docId -> bool (checkbox ticked)
  const [confirmChecked, setConfirmChecked] = useState({});
  const [confirming, setConfirming] = useState({});

  useEffect(() => {
    if (!user?.restaurant_id) {
      setLoading(false);
      return;
    }
    employeeAPI
      .getDocuments(user.restaurant_id)
      .then(res => setDocuments(res.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user]);

  const handleOpenPdf = async (doc) => {
    try {
      const { data } = await documentAPI.getDownloadUrl(doc.id);
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Failed to open document');
    }
  };

  const handleConfirmRead = async (doc) => {
    if (!confirmChecked[doc.id]) {
      toast.error('Please tick the confirmation checkbox first');
      return;
    }
    setConfirming(s => ({ ...s, [doc.id]: true }));
    try {
      await documentAPI.acknowledge(doc.id);
      toast.success('Marked as read');
      setDocuments(prev =>
        prev.map(d =>
          d.id === doc.id
            ? { ...d, acknowledged_by_user_ids: [...(d.acknowledged_by_user_ids || []), user?.id] }
            : d
        )
      );
      setConfirmChecked(s => ({ ...s, [doc.id]: false }));
    } catch {
      toast.error('Failed to confirm. Please try again.');
    } finally {
      setConfirming(s => ({ ...s, [doc.id]: false }));
    }
  };

  return (
    <EmployeeLayout>
      <div className="p-6 space-y-6" data-testid="employee-documents">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Documents
        </h1>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
          </div>
        ) : documents.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <FileText className="mx-auto text-slate-300 mb-3" size={40} />
            <p className="text-slate-500">No documents shared yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map(doc => {
              const isRead = (doc.acknowledged_by_user_ids || []).includes(user?.id);
              const checked = !!confirmChecked[doc.id];
              const busy = !!confirming[doc.id];
              return (
                <div
                  key={doc.id}
                  className="bg-white rounded-xl border border-slate-200 shadow-sm p-5"
                  data-testid="document-item"
                >
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <FileText size={20} className="text-slate-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 break-words" data-testid="document-filename">
                          {doc.filename}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {doc.uploaded_by_name || '—'} · {timeAgo(doc.created_at)}
                        </p>
                      </div>
                    </div>
                    {isRead ? (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1"
                        data-testid="document-read-badge"
                      >
                        <CheckCircle size={12} /> Read
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1"
                        data-testid="document-unread-badge"
                      >
                        <Circle size={12} /> Unread
                      </span>
                    )}
                  </div>

                  <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-slate-100 pt-3">
                    <button
                      type="button"
                      onClick={() => handleOpenPdf(doc)}
                      className="inline-flex items-center justify-center gap-1.5 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50 transition-colors min-h-[40px] w-full sm:w-auto"
                      data-testid="document-open-pdf-btn"
                    >
                      <ExternalLink size={14} /> Open PDF
                    </button>

                    {!isRead && (
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                        <label className="inline-flex items-center gap-2 text-sm text-slate-700 select-none cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={e =>
                              setConfirmChecked(s => ({ ...s, [doc.id]: e.target.checked }))
                            }
                            className="h-4 w-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                            data-testid="document-confirm-checkbox"
                          />
                          I confirm I read this document
                        </label>
                        <button
                          type="button"
                          onClick={() => handleConfirmRead(doc)}
                          disabled={!checked || busy}
                          className="inline-flex items-center justify-center gap-1 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 disabled:cursor-not-allowed rounded-lg px-3 py-2 transition-colors min-h-[40px] w-full sm:w-auto"
                          data-testid="document-confirm-read-btn"
                        >
                          <CheckCircle size={14} />
                          {busy ? 'Confirming…' : 'Confirm Read'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </EmployeeLayout>
  );
}
